import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { GmAssignmentService } from '@org/shared-services';
import {
  GetMyAssignedTenantsResponse,
  GetTenantGMsResponse,
  GetAvailableGMsResponse,
  AssignGMToTenantRequest,
  AssignGMToTenantResponse,
  DeactivateAssignmentResponse,
  Tenant,
  User,
  Role,
  AssignmentType
} from '@org/interfaces';
import { CommonModule } from '@angular/common';

interface TenantRow {
  tenant: Tenant & { marketAreaId: string; marketAreaName: string; currentGM?: User };
  gms: (User & {
    roles: Role[];
    assignmentId: string;
    assignmentType: AssignmentType;
    startDate: string;
    endDate?: string;
    reason: string;
    assignedBy: User;
  })[];
}

@Component({
  selector: 'app-gm-assignment',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './gm-assignment.component.html',
  styleUrls: ['./gm-assignment.component.scss']
})
export class GmAssignmentComponent implements OnInit {
  // UI state
  showAssignmentModal = false;
  showConfirmModal = false;
  showHistoryModal = false;

  // Loading and error states
  isLoading = false;
  isLoadingGMs = false;
  isLoadingAction = false;
  isLoadingHistory = false;
  error: any = {};

  // Data
  tenantRows: TenantRow[] = [];
  availableGMs: (User & { roles: Role[]; tenant: Tenant })[] = [];
  selectedTenant: TenantRow | null = null;
  selectedGM: (User & { assignmentId: string }) | null = null;
  assignmentHistory: any[] = [];
  tenantSearch = '';
  gmSearch = '';

  // Filters
  tenantSearchControl: FormControl = new FormControl('');
  gmSearchControl: FormControl = new FormControl('');
  marketAreaControl: FormControl = new FormControl('all');
  assignmentStatusControl: FormControl = new FormControl('all');

  // Forms
  assignmentForm: FormGroup;

  // Filter options
  marketAreas: string[] = [];
  assignmentStatusOptions = [
    { value: 'all', label: 'All' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'unassigned', label: 'Unassigned' }
  ];

  // Bulk assignment state
  selectedTenantIds: Set<string> = new Set();
  showBulkAssignModal = false;
  bulkAssignForm: FormGroup = this.fb.group({
    gmUserId: ['', Validators.required],
    assignmentType: ['permanent'],
    reason: [''],
    notes: [''],
    endDate: ['']
  });
  bulkAssignResult: { success: number; failed: number } | null = null;

  constructor(
    private gmAssignmentService: GmAssignmentService,
    private fb: FormBuilder
  ) {
    this.assignmentForm = this.fb.group({
      tenantId: ['', Validators.required],
      gmUserId: ['', Validators.required],
      reason: [''],
      assignmentType: ['permanent'],
      notes: [''],
      endDate: ['']
    });
  }

  ngOnInit(): void {
    this.loadTenantsAndGMs();
    // Subscribe to tenant search changes
    this.tenantSearchControl.valueChanges.subscribe((val: string) => {
      this.tenantSearch = val || '';
    });
    // Subscribe to GM search changes
    this.gmSearchControl.valueChanges.subscribe((val: string) => {
      this.gmSearch = val || '';
    });
    // Subscribe to market area and assignment status changes (no-op, just for reactivity)
    this.marketAreaControl.valueChanges.subscribe(() => {});
    this.assignmentStatusControl.valueChanges.subscribe(() => {});
  }

  // Load all tenants managed by the AM and their assigned GMs
  loadTenantsAndGMs() {
    this.isLoading = true;
    this.gmAssignmentService.getMyAssignedTenants().subscribe({
      next: (res: GetMyAssignedTenantsResponse) => {
        const tenants = res.data.tenants;
        const rows: TenantRow[] = [];
        let loaded = 0;
        if (!tenants.length) {
          this.tenantRows = [];
          this.marketAreas = [];
          this.isLoading = false;
          return;
        }
        tenants.forEach((tenant) => {
          this.gmAssignmentService.getTenantGMs(tenant._id!).subscribe({
            next: (gmRes: GetTenantGMsResponse) => {
              rows.push({
                tenant,
                gms: gmRes.data.gms
              });
              loaded++;
              if (loaded === tenants.length) {
                this.tenantRows = rows;
                // Compute unique market areas for filter
                this.marketAreas = Array.from(new Set(rows.map(r => r.tenant.marketAreaName))).sort();
                this.isLoading = false;
              }
            },
            error: () => {
              loaded++;
              if (loaded === tenants.length) {
                this.tenantRows = rows;
                this.marketAreas = Array.from(new Set(rows.map(r => r.tenant.marketAreaName))).sort();
                this.isLoading = false;
              }
            }
          });
        });
      },
      error: (err: any) => {
        this.error.tenants = err.message || 'Failed to load tenants.';
        this.tenantRows = [];
        this.marketAreas = [];
        this.isLoading = false;
      }
    });
  }

  // Open the assign GM modal for a tenant
  openAssignmentModal(tenant: TenantRow) {
    this.selectedTenant = tenant;
    this.assignmentForm.patchValue({ tenantId: tenant.tenant._id, gmUserId: '' });
    this.availableGMs = [];
    this.showAssignmentModal = true;
    this.loadAvailableGMs();
  }

  closeAssignmentModal() {
    this.showAssignmentModal = false;
    this.assignmentForm.reset();
    this.selectedTenant = null;
    this.availableGMs = [];
  }

  // Load available GMs for assignment
  loadAvailableGMs() {
    this.isLoadingGMs = true;
    this.gmAssignmentService.getAvailableGMs().subscribe({
      next: (res: GetAvailableGMsResponse) => {
        this.availableGMs = res.data.gms;
        this.isLoadingGMs = false;
      },
      error: () => {
        this.availableGMs = [];
        this.isLoadingGMs = false;
      }
    });
  }

  // Assign a GM to the selected tenant
  submitAssignment() {
    if (this.assignmentForm.invalid) return;
    this.isLoadingAction = true;
    const payload: AssignGMToTenantRequest = this.assignmentForm.value;
    this.gmAssignmentService.assignGMToTenant(payload).subscribe({
      next: (res: AssignGMToTenantResponse) => {
        this.loadTenantsAndGMs();
        this.closeAssignmentModal();
        this.isLoadingAction = false;
      },
      error: (err: any) => {
        this.error.action = err.message || 'Failed to assign GM.';
        this.isLoadingAction = false;
      }
    });
  }

  // Open remove confirmation modal for a GM assignment
  openRemoveModal(gm: User & { assignmentId: string }) {
    this.selectedGM = gm;
    this.showConfirmModal = true;
  }

  // Remove/deactivate a GM assignment
  executeRemoveAssignment() {
    if (!this.selectedGM) return;
    this.isLoadingAction = true;
    this.gmAssignmentService.deactivateAssignment(this.selectedGM.assignmentId).subscribe({
      next: (res: DeactivateAssignmentResponse) => {
        this.loadTenantsAndGMs();
        this.showConfirmModal = false;
        this.selectedGM = null;
        this.isLoadingAction = false;
      },
      error: (err: any) => {
        this.error.action = err.message || 'Failed to remove assignment.';
        this.isLoadingAction = false;
      }
    });
  }

  // Open assignment history modal (placeholder)
  openHistoryModal(gm: User & { assignmentId: string }) {
    this.assignmentHistory = [];
    this.showHistoryModal = true;
  }

  closeHistoryModal() {
    this.showHistoryModal = false;
    this.assignmentHistory = [];
    this.selectedGM = null;
  }

  // Helpers for display
  getTenantName(tenant: Tenant) {
    return tenant?.name || 'Unknown Tenant';
  }

  getGMName(gm: User) {
    return gm?.name || gm?.email || 'Unknown GM';
  }

  getAssignmentTypeLabel(type: AssignmentType) {
    switch (type) {
      case AssignmentType.PERMANENT: return 'Permanent';
      case AssignmentType.TEMPORARY: return 'Temporary';
      case AssignmentType.BACKUP: return 'Backup';
      case AssignmentType.SUPPORT: return 'Support';
      default: return type;
    }
  }

  // Get a CSS class for assignment type badge
  getAssignmentTypeClass(type: AssignmentType): string {
    switch (type) {
      case AssignmentType.PERMANENT: return 'badge-permanent';
      case AssignmentType.TEMPORARY: return 'badge-temporary';
      case AssignmentType.BACKUP: return 'badge-backup';
      case AssignmentType.SUPPORT: return 'badge-support';
      default: return '';
    }
  }

  // Get the number of active assignments for a GM (across all tenants)
  getGMWorkload(gmId: string): number {
    let count = 0;
    for (const row of this.tenantRows) {
      count += row.gms.filter(gm => gm._id === gmId && !gm.endDate).length;
    }
    return count;
  }

  // Get a label for GM workload (e.g., '3 locs')
  getGMWorkloadLabel(gmId: string): string {
    const count = this.getGMWorkload(gmId);
    return count === 1 ? '1 loc' : `${count} locs`;
  }

  // Filtering/search helpers
  get filteredTenantRows() {
    let rows = this.tenantRows;
    // Filter by search
    if (this.tenantSearch) {
      rows = rows.filter(row =>
        row.tenant.name.toLowerCase().includes(this.tenantSearch.toLowerCase())
      );
    }
    // Filter by market area
    const selectedMarketArea = this.marketAreaControl.value;
    if (selectedMarketArea && selectedMarketArea !== 'all') {
      rows = rows.filter(row => row.tenant.marketAreaName === selectedMarketArea);
    }
    // Filter by assignment status
    const status = this.assignmentStatusControl.value;
    if (status === 'assigned') {
      rows = rows.filter(row => row.gms.length > 0);
    } else if (status === 'unassigned') {
      rows = rows.filter(row => row.gms.length === 0);
    }
    return rows;
  }

  get filteredGMs() {
    if (!this.gmSearch) return this.availableGMs;
    return this.availableGMs.filter(gm =>
      gm.name.toLowerCase().includes(this.gmSearch.toLowerCase()) ||
      gm.email.toLowerCase().includes(this.gmSearch.toLowerCase())
    );
  }

  // Toggle selection for a single tenant
  toggleTenantSelection(tenantId: string | undefined) {
    if (!tenantId) return;
    if (this.selectedTenantIds.has(tenantId)) {
      this.selectedTenantIds.delete(tenantId);
    } else {
      this.selectedTenantIds.add(tenantId);
    }
  }

  // Toggle select all tenants in the filtered list
  toggleSelectAll() {
    if (this.isAllSelected()) {
      this.selectedTenantIds.clear();
    } else {
      for (const row of this.filteredTenantRows) {
        if (row.tenant._id) {
          this.selectedTenantIds.add(row.tenant._id);
        }
      }
    }
  }

  // Check if a tenant is selected
  isTenantSelected(tenantId: string): boolean {
    return this.selectedTenantIds.has(tenantId);
  }

  // Check if all filtered tenants are selected
  isAllSelected(): boolean {
    return (
      this.filteredTenantRows.length > 0 &&
      this.filteredTenantRows
        .filter(row => !!row.tenant._id)
        .every(row => this.selectedTenantIds.has(row.tenant._id!))
    );
  }

  // Open/close bulk assign modal
  openBulkAssignModal() {
    this.bulkAssignForm.reset({ gmUserId: '', assignmentType: 'permanent', reason: '', notes: '', endDate: '' });
    this.showBulkAssignModal = true;
    this.bulkAssignResult = null;
    this.loadAvailableGMs();
  }
  closeBulkAssignModal() {
    this.showBulkAssignModal = false;
    this.bulkAssignResult = null;
  }

  // Bulk assign GM to all selected tenants
  submitBulkAssignment() {
    if (this.bulkAssignForm.invalid || this.selectedTenantIds.size === 0) return;
    const { gmUserId, assignmentType, reason, notes, endDate } = this.bulkAssignForm.value;
    let success = 0;
    let failed = 0;
    const total = this.selectedTenantIds.size;
    this.isLoadingAction = true;
    const assignRequests = Array.from(this.selectedTenantIds)
      .filter((tenantId): tenantId is string => typeof tenantId === 'string')
      .map((tenantId: string) => {
        const payload = { tenantId: tenantId as string, gmUserId, assignmentType, reason, notes, endDate };
        return this.gmAssignmentService.assignGMToTenant(payload).toPromise()
          .then(() => { success++; })
          .catch(() => { failed++; });
      });
    Promise.all(assignRequests).then(() => {
      this.bulkAssignResult = { success, failed };
      this.isLoadingAction = false;
      this.loadTenantsAndGMs();
      this.selectedTenantIds.clear();
    });
  }
} 